import { useState } from 'react';
import type { AccountInfo } from '@azure/msal-browser';

import { getSecureValue, navigateTo } from '@helpers';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { msalInstance, MSAL_SCOPES } from '@config/msalConfig';
import { switchWebAccount, removeWebAccount } from '@reducers/user/webMultiAccount';
import { unlogin } from '@reducers/utils';
import { getAccountInitial } from '@components/webHeader/profile/hooks/useManageProfileDropdown';

export interface UseManageAccountChooserParams {
  updateTokenState: (token: string) => void;
}

export const useManageAccountChooser = ({ updateTokenState }: UseManageAccountChooserParams) => {
  const dispatch = useAppDispatch();

  const [switchingAccount, setSwitchingAccount] = useState<string | false>(false);
  const [removingMode, setRemovingMode] = useState(false);
  const [accounts, setAccounts] = useState<AccountInfo[]>(() => msalInstance.getAllAccounts());

  const refreshAccounts = () => setAccounts(msalInstance.getAllAccounts());

  const handleSelectAccount = async (homeAccountId: string) => {
    setSwitchingAccount(homeAccountId);
    try {
      await dispatch(switchWebAccount(homeAccountId));
      const token = getSecureValue('token');
      if (token) {
        updateTokenState(token as string);
        navigateTo('Authorized');
      }
    } catch (e) {
      // switchWebAccount threw → reinitializeAppState failed after clear().
      // Don't navigate to Authorized (it would render stuck). Drop back to a clean login.
      dispatch(unlogin());
    } finally {
      setSwitchingAccount(false);
    }
  };

  const handleUseAnotherAccount = () => {
    msalInstance.loginRedirect({ scopes: MSAL_SCOPES, prompt: 'login' });
  };

  const handleRemoveAccount = async (homeAccountId: string) => {
    try {
      await dispatch(removeWebAccount(homeAccountId));
    } catch (e) {
      // removeWebAccount dispatches switchWebAccount when removing the active account;
      // that rethrows on reinitializeAppState failure. Recover via unlogin so the UI
      // doesn't strand mid-remove.
      dispatch(unlogin());
    } finally {
      refreshAccounts();
    }
  };

  const toggleRemovingMode = () => setRemovingMode((prev) => !prev);

  return {
    accounts,
    switchingAccount,
    removingMode,
    handleSelectAccount,
    handleUseAnotherAccount,
    handleRemoveAccount,
    toggleRemovingMode,
  };
};

export { getAccountInitial };
